import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import api from '../api';

const S = {
  page: { padding: '24px', maxWidth: 1200, margin: '0 auto' },
  header: { marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 },
  title: { fontSize: 22, fontWeight: 700, marginBottom: 4 },
  subtitle: { fontSize: 14, color: '#8B99AE' },
  tabs: { display: 'flex', gap: 4, marginBottom: 20, borderBottom: '1px solid #1E293B' },
  tab: (active) => ({
    padding: '10px 18px', fontSize: 13, fontWeight: active ? 600 : 400,
    color: active ? '#3B82F6' : '#64748B', background: 'none', border: 'none',
    borderBottom: active ? '2px solid #3B82F6' : '2px solid transparent',
    cursor: 'pointer', fontFamily: 'inherit',
  }),
  card: { background: '#111827', border: '1px solid #1E293B', borderRadius: 10, padding: 20, marginBottom: 16 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { padding: '10px 12px', textAlign: 'right', borderBottom: '1px solid #1E293B', color: '#64748B', fontWeight: 600, fontSize: 12 },
  td: { padding: '10px 12px', borderBottom: '1px solid rgba(30,41,59,0.5)', verticalAlign: 'top' },
  btn: (variant = 'primary', disabled = false) => ({
    padding: '8px 16px', borderRadius: 8, border: 'none', fontSize: 13, fontWeight: 600,
    cursor: disabled ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
    opacity: disabled ? 0.5 : 1,
    ...(variant === 'primary' ? { background: '#3B82F6', color: '#fff' } :
      variant === 'secondary' ? { background: '#1E293B', color: '#94A3B8', border: '1px solid #2D3A4F' } :
      variant === 'danger' ? { background: 'rgba(239,68,68,0.12)', color: '#EF4444' } :
      { background: 'rgba(34,197,94,0.12)', color: '#22C55E' }),
  }),
  smallBtn: { background: 'none', border: '1px solid #2D3A4F', borderRadius: 6, color: '#94A3B8', fontSize: 11, padding: '3px 8px', cursor: 'pointer', fontFamily: 'inherit' },
  stat: { display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 20 },
  statBox: { background: '#1A2237', borderRadius: 8, padding: '12px 16px', minWidth: 120, flex: 1 },
  statLabel: { fontSize: 11, color: '#64748B', marginBottom: 4 },
  statValue: { fontSize: 22, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace" },
  input: { width: '100%', background: '#0B0F1A', border: '1px solid #1E293B', borderRadius: 6, padding: '8px 12px', color: '#E2E8F0', fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' },
  label: { fontSize: 12, color: '#94A3B8', marginBottom: 4, display: 'block', marginTop: 10 },
  overlay: { position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 },
  modal: { background: '#111827', border: '1px solid #1E293B', borderRadius: 12, padding: 24, width: 560, maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto' },
  link: { color: '#3B82F6', textDecoration: 'none', direction: 'ltr', display: 'inline-block', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom' },
  alert: { padding: '12px 16px', borderRadius: 8, marginBottom: 12, fontSize: 13, background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)', color: '#F59E0B' },
};

const STATUS = {
  ok: { label: 'תקין', color: '#22C55E', bg: 'rgba(34,197,94,0.12)' },
  missing: { label: 'חסר', color: '#EF4444', bg: 'rgba(239,68,68,0.12)' },
  unreachable: { label: 'לא זמין', color: '#EF4444', bg: 'rgba(239,68,68,0.12)' },
  nofollow_mismatch: { label: 'הפך ל-nofollow', color: '#F59E0B', bg: 'rgba(245,158,11,0.12)' },
  target_changed: { label: 'יעד השתנה', color: '#F59E0B', bg: 'rgba(245,158,11,0.12)' },
  unchecked: { label: 'לא נבדק', color: '#64748B', bg: 'rgba(100,116,139,0.12)' },
};

function badge(text, color, bg) {
  return <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, color, background: bg }}>{text}</span>;
}

function currentUserName() {
  try { return (JSON.parse(localStorage.getItem('maxrank_user') || '{}').name) || ''; } catch (e) { return ''; }
}

export default function SiteLinks() {
  const { id } = useParams();
  const [tab, setTab] = useState('backlinks');
  const [data, setData] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [checking, setChecking] = useState(null);
  const [editLink, setEditLink] = useState(null);   // backlink object or {} for new
  const [editContact, setEditContact] = useState(null);
  const [showImport, setShowImport] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [d, c] = await Promise.all([api.getBacklinks(id), api.getLinkContacts()]);
      setData(d); setContacts(c);
    } catch (e) { setError(e.message); }
    setLoading(false);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const handleCheck = async (bid) => {
    setChecking(bid);
    try { await api.checkBacklink(bid); await load(); }
    catch (e) { setError(e.message); }
    setChecking(null);
  };

  const handleDeleteLink = async (bid) => {
    if (!window.confirm('למחוק את הקישור?')) return;
    try { await api.deleteBacklink(bid); await load(); } catch (e) { setError(e.message); }
  };

  const handleDeleteContact = async (cid) => {
    if (!window.confirm('למחוק את איש הקשר?')) return;
    try { await api.deleteLinkContact(cid); await load(); } catch (e) { setError(e.message); }
  };

  if (loading) return <div style={S.page}><div style={S.card}>טוען נתונים...</div></div>;

  const s = data?.summary || {};
  const rows = data?.backlinks || [];

  return (
    <div style={S.page}>
      <div style={S.header}>
        <div>
          <div style={S.title}>קישורים — {data?.site_name}</div>
          <div style={S.subtitle}>{s.total || 0} קישורים · {s.ok || 0} תקינים · {s.broken || 0} שבורים</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={S.btn('secondary')} onClick={() => setShowImport(true)}>ייבוא מזפו</button>
          {tab === 'backlinks'
            ? <button style={S.btn('primary')} onClick={() => setEditLink({})}>+ הוסף קישור</button>
            : <button style={S.btn('primary')} onClick={() => setEditContact({})}>+ הוסף איש קשר</button>}
        </div>
      </div>

      {error && <div style={S.alert}>{error}</div>}

      <div style={S.tabs}>
        <button style={S.tab(tab === 'backlinks')} onClick={() => setTab('backlinks')}>קישורים</button>
        <button style={S.tab(tab === 'contacts')} onClick={() => setTab('contacts')}>אנשי קשר ({contacts.length})</button>
      </div>

      {tab === 'backlinks' && (
        <>
          <div style={S.stat}>
            <div style={S.statBox}><div style={S.statLabel}>סה"כ</div><div style={S.statValue}>{s.total || 0}</div></div>
            <div style={S.statBox}><div style={S.statLabel}>תקינים</div><div style={{ ...S.statValue, color: '#22C55E' }}>{s.ok || 0}</div></div>
            <div style={S.statBox}><div style={S.statLabel}>שבורים</div><div style={{ ...S.statValue, color: '#EF4444' }}>{s.broken || 0}</div></div>
            <div style={S.statBox}><div style={S.statLabel}>nofollow</div><div style={{ ...S.statValue, color: '#F59E0B' }}>{s.nofollow || 0}</div></div>
          </div>

          <div style={S.card}>
            {rows.length === 0 ? (
              <div style={{ color: '#64748B', textAlign: 'center', padding: 24 }}>אין קישורים עדיין. הוסף קישור או ייבא מזפו.</div>
            ) : (
              <table style={S.table}>
                <thead><tr>
                  <th style={S.th}>עמוד מקור</th>
                  <th style={S.th}>יעד</th>
                  <th style={S.th}>אנקור</th>
                  <th style={{ ...S.th, textAlign: 'center' }}>סוג</th>
                  <th style={{ ...S.th, textAlign: 'center' }}>סטטוס</th>
                  <th style={S.th}>איש קשר</th>
                  <th style={S.th}>הוסף</th>
                  <th style={{ ...S.th, textAlign: 'center' }}>פעולות</th>
                </tr></thead>
                <tbody>
                  {rows.map((b) => {
                    const st = STATUS[b.check_status] || STATUS.unchecked;
                    return (
                      <tr key={b.id}>
                        <td style={S.td}><a href={b.source_url} target="_blank" rel="noopener noreferrer" style={S.link}>{b.source_domain || b.source_url}</a></td>
                        <td style={S.td}><a href={b.target_url} target="_blank" rel="noopener noreferrer" style={S.link}>{b.target_url}</a></td>
                        <td style={S.td}>{b.anchor_text || '-'}</td>
                        <td style={{ ...S.td, textAlign: 'center' }}>
                          {b.expected_nofollow ? badge('nofollow', '#F59E0B', 'rgba(245,158,11,0.12)') : badge('follow', '#3B82F6', 'rgba(59,130,246,0.12)')}
                        </td>
                        <td style={{ ...S.td, textAlign: 'center' }}>
                          {badge(st.label, st.color, st.bg)}
                          {b.last_checked_at && <div style={{ fontSize: 10, color: '#4B5C73', marginTop: 3 }}>{new Date(b.last_checked_at).toLocaleDateString('he-IL')}</div>}
                        </td>
                        <td style={S.td}>
                          {b.contact_email
                            ? <a href={'mailto:' + b.contact_email} style={{ color: '#3B82F6', textDecoration: 'none' }}>{b.contact_name || b.contact_email}</a>
                            : (b.contact_name || '-')}
                        </td>
                        <td style={{ ...S.td, fontSize: 12, color: '#64748B' }}>{b.added_by || '-'}</td>
                        <td style={{ ...S.td, textAlign: 'center', whiteSpace: 'nowrap' }}>
                          <button style={S.smallBtn} onClick={() => handleCheck(b.id)} disabled={checking === b.id}>{checking === b.id ? '...' : 'בדוק'}</button>{' '}
                          <button style={S.smallBtn} onClick={() => setEditLink(b)}>ערוך</button>{' '}
                          <button style={{ ...S.smallBtn, color: '#EF4444', borderColor: '#5b2333' }} onClick={() => handleDeleteLink(b.id)}>מחק</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {tab === 'contacts' && (
        <div style={S.card}>
          {contacts.length === 0 ? (
            <div style={{ color: '#64748B', textAlign: 'center', padding: 24 }}>אין אנשי קשר עדיין.</div>
          ) : (
            <table style={S.table}>
              <thead><tr>
                <th style={S.th}>שם</th><th style={S.th}>אימייל</th><th style={S.th}>טלפון</th>
                <th style={S.th}>אתרים קשורים</th><th style={{ ...S.th, textAlign: 'center' }}>פעולות</th>
              </tr></thead>
              <tbody>
                {contacts.map((c) => (
                  <tr key={c.id}>
                    <td style={S.td}>{c.name || '-'}</td>
                    <td style={S.td}>{c.email ? <a href={'mailto:' + c.email} style={{ color: '#3B82F6', textDecoration: 'none' }}>{c.email}</a> : '-'}</td>
                    <td style={S.td}>{c.phone || '-'}</td>
                    <td style={{ ...S.td, fontSize: 12, color: '#94A3B8' }}>{(c.related_domains || []).join(', ') || '-'}</td>
                    <td style={{ ...S.td, textAlign: 'center', whiteSpace: 'nowrap' }}>
                      <button style={S.smallBtn} onClick={() => setEditContact(c)}>ערוך</button>{' '}
                      <button style={{ ...S.smallBtn, color: '#EF4444', borderColor: '#5b2333' }} onClick={() => handleDeleteContact(c.id)}>מחק</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {editLink && <BacklinkModal siteId={id} backlink={editLink} contacts={contacts} onClose={() => setEditLink(null)} onSaved={() => { setEditLink(null); load(); }} />}
      {editContact && <ContactModal contact={editContact} onClose={() => setEditContact(null)} onSaved={() => { setEditContact(null); load(); }} />}
      {showImport && <ImportModal onClose={() => setShowImport(false)} onDone={() => { setShowImport(false); load(); }} />}
    </div>
  );
}

// ─── Add/Edit Backlink modal ──────────────────

function BacklinkModal({ siteId, backlink, contacts, onClose, onSaved }) {
  const isNew = !backlink.id;
  const [form, setForm] = useState({
    source_url: backlink.source_url || '',
    target_url: backlink.target_url || '',
    anchor_text: backlink.anchor_text || '',
    expected_nofollow: !!backlink.expected_nofollow,
    contact_id: backlink.contact_id || '',
    added_by: backlink.added_by || (isNew ? currentUserName() : ''),
    date_added: backlink.date_added || '',
    notes: backlink.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });

  const save = async () => {
    if (!form.source_url.trim() || !form.target_url.trim()) { setErr('נדרשים עמוד מקור ויעד'); return; }
    setSaving(true); setErr('');
    const payload = {
      ...form,
      contact_id: form.contact_id ? parseInt(form.contact_id) : null,
      date_added: form.date_added || null,
    };
    try {
      if (isNew) await api.createBacklink(siteId, payload);
      else await api.updateBacklink(backlink.id, payload);
      onSaved();
    } catch (e) { setErr(e.message); setSaving(false); }
  };

  return (
    <div style={S.overlay} onClick={onClose}>
      <div style={S.modal} onClick={(e) => e.stopPropagation()}>
        <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>{isNew ? 'הוספת קישור' : 'עריכת קישור'}</div>
        {err && <div style={S.alert}>{err}</div>}
        <label style={S.label}>עמוד מקור (הכתובת שבה נמצא הקישור)</label>
        <input style={{ ...S.input, direction: 'ltr' }} value={form.source_url} onChange={set('source_url')} placeholder="https://example.com/article" />
        <label style={S.label}>יעד (העמוד שלנו שאליו הקישור מפנה)</label>
        <input style={{ ...S.input, direction: 'ltr' }} value={form.target_url} onChange={set('target_url')} placeholder="https://oursite.co.il/page" />
        <label style={S.label}>טקסט עוגן (אנקור)</label>
        <input style={S.input} value={form.anchor_text} onChange={set('anchor_text')} />
        <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginTop: 12 }}>
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13, cursor: 'pointer' }}>
            <input type="checkbox" checked={form.expected_nofollow} onChange={set('expected_nofollow')} /> nofollow
          </label>
        </div>
        <label style={S.label}>איש קשר</label>
        <select style={S.input} value={form.contact_id} onChange={set('contact_id')}>
          <option value="">— ללא —</option>
          {contacts.map((c) => <option key={c.id} value={c.id}>{c.name || c.email}</option>)}
        </select>
        <label style={S.label}>מי הוסיף</label>
        <input style={S.input} value={form.added_by} onChange={set('added_by')} />
        <label style={S.label}>תאריך הוספה</label>
        <input type="date" style={S.input} value={form.date_added || ''} onChange={set('date_added')} />
        <label style={S.label}>הערות</label>
        <textarea style={{ ...S.input, minHeight: 60 }} value={form.notes} onChange={set('notes')} />
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
          <button style={S.btn('secondary')} onClick={onClose}>ביטול</button>
          <button style={S.btn('primary', saving)} onClick={save} disabled={saving}>{saving ? 'שומר...' : 'שמור'}</button>
        </div>
      </div>
    </div>
  );
}

// ─── Add/Edit Contact modal ───────────────────

function ContactModal({ contact, onClose, onSaved }) {
  const isNew = !contact.id;
  const [form, setForm] = useState({
    name: contact.name || '', email: contact.email || '', phone: contact.phone || '',
    related_domains: (contact.related_domains || []).join(', '), notes: contact.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    if (!form.name.trim() && !form.email.trim()) { setErr('נדרש שם או אימייל'); return; }
    setSaving(true); setErr('');
    const payload = {
      name: form.name || null, email: form.email || null, phone: form.phone || null,
      related_domains: form.related_domains.split(',').map((x) => x.trim()).filter(Boolean),
      notes: form.notes || null,
    };
    try {
      if (isNew) await api.createLinkContact(payload);
      else await api.updateLinkContact(contact.id, payload);
      onSaved();
    } catch (e) { setErr(e.message); setSaving(false); }
  };

  return (
    <div style={S.overlay} onClick={onClose}>
      <div style={S.modal} onClick={(e) => e.stopPropagation()}>
        <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>{isNew ? 'הוספת איש קשר' : 'עריכת איש קשר'}</div>
        {err && <div style={S.alert}>{err}</div>}
        <label style={S.label}>שם</label>
        <input style={S.input} value={form.name} onChange={set('name')} />
        <label style={S.label}>אימייל</label>
        <input style={{ ...S.input, direction: 'ltr' }} value={form.email} onChange={set('email')} />
        <label style={S.label}>טלפון</label>
        <input style={{ ...S.input, direction: 'ltr' }} value={form.phone} onChange={set('phone')} />
        <label style={S.label}>אתרים קשורים (מופרדים בפסיק)</label>
        <input style={{ ...S.input, direction: 'ltr' }} value={form.related_domains} onChange={set('related_domains')} />
        <label style={S.label}>הערות</label>
        <textarea style={{ ...S.input, minHeight: 60 }} value={form.notes} onChange={set('notes')} />
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
          <button style={S.btn('secondary')} onClick={onClose}>ביטול</button>
          <button style={S.btn('primary', saving)} onClick={save} disabled={saving}>{saving ? 'שומר...' : 'שמור'}</button>
        </div>
      </div>
    </div>
  );
}

// ─── Zefo import modal ────────────────────────

function ImportModal({ onClose, onDone }) {
  const [linksFile, setLinksFile] = useState(null);
  const [contactsFile, setContactsFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const analyze = async () => {
    setBusy(true); setErr(''); setPreview(null);
    try { setPreview(await api.analyzeLinksImport(linksFile, contactsFile)); }
    catch (e) { setErr(e.message); }
    setBusy(false);
  };

  const commit = async () => {
    setBusy(true); setErr('');
    try { await api.commitLinksImport(linksFile, contactsFile); onDone(); }
    catch (e) { setErr(e.message); setBusy(false); }
  };

  return (
    <div style={S.overlay} onClick={onClose}>
      <div style={S.modal} onClick={(e) => e.stopPropagation()}>
        <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>ייבוא מזפו</div>
        <div style={{ fontSize: 12, color: '#8B99AE', marginBottom: 12 }}>
          העלה את קבצי ה-CSV שיוצאו מזפו. הייבוא מזהה לכל שורה את האתר לפי הדומיין ומשייך אותה אוטומטית. שורות של אתרים שלא קיימים במערכת ידווחו ולא ייובאו.
        </div>
        {err && <div style={S.alert}>{err}</div>}
        <label style={S.label}>קובץ קישורים (viewLinksTable.csv)</label>
        <input type="file" accept=".csv" onChange={(e) => { setLinksFile(e.target.files[0]); setPreview(null); }} style={{ color: '#94A3B8', fontSize: 13 }} />
        <label style={S.label}>קובץ אנשי קשר (contacts-table.csv)</label>
        <input type="file" accept=".csv" onChange={(e) => { setContactsFile(e.target.files[0]); setPreview(null); }} style={{ color: '#94A3B8', fontSize: 13 }} />

        {preview && (
          <div style={{ ...S.statBox, marginTop: 16, background: '#0B0F1A' }}>
            <div style={{ fontSize: 13, lineHeight: 1.8 }}>
              <div>אנשי קשר: <b>{preview.contacts_imported}</b></div>
              <div>קישורים שותאמו לאתר: <b>{preview.matched}</b> (חדשים {preview.would_add} · עדכון {preview.would_update})</div>
              <div style={{ color: '#F59E0B' }}>דולגו (אתר לא נמצא): <b>{(preview.skipped_no_site || []).length}</b></div>
              {(preview.skipped_no_site || []).length > 0 &&
                <div style={{ fontSize: 11, color: '#64748B', marginTop: 4, direction: 'ltr', maxHeight: 120, overflowY: 'auto' }}>
                  {preview.skipped_no_site.join(', ')}
                </div>}
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
          <button style={S.btn('secondary')} onClick={onClose}>סגור</button>
          <button style={S.btn('secondary', busy || (!linksFile && !contactsFile))} onClick={analyze} disabled={busy || (!linksFile && !contactsFile)}>
            {busy ? '...' : 'בדיקה מקדימה'}
          </button>
          <button style={S.btn('primary', busy || !preview)} onClick={commit} disabled={busy || !preview}>
            {busy ? 'מייבא...' : 'ייבא'}
          </button>
        </div>
      </div>
    </div>
  );
}
